import React, {useContext, useEffect, useRef, useState} from "react";
import MyContext from "../../../Context";
import AdminSidebar from "../components/sidebar/admin_sidebar";
import PageTitle from "../../../components/page_title/page_title";
import TableComponent from "../../../components/table/table";
import axiosInstance from "../../../AxiosInstance";
import Modal from "../../../components/modal/modal";
import {LogoInput, TextInput} from "../../../components/input/inputs";
import styles from "../../settings/styles/settings.module.css";
import sidebarStyles from "../components/sidebar/admin_sidebar.module.css";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit, faMailForward, faToggleOn, faToggleOff, faTrash, faRightToBracket} from "@fortawesome/free-solid-svg-icons";
import {useTranslation} from "react-i18next";


function PopupSettings({currentCorporateGroup, setShowPopup, setShowModal, setForm, setRows, setShowDeleteModal, setInfoMessage}) {
    const {t} = useTranslation();
    const sendInvite = () => {
        setShowPopup(false);
        axiosInstance.post('user_invite/', {
            corporate_group_id: currentCorporateGroup.id,
            email: currentCorporateGroup.contact_email,
            super_admin_invite: true,
        }).then(() => {
            setInfoMessage(t('admin.invitationSent'));
        }).catch((err) => {
            const code = err?.response?.data?.error;
            if (code === 'USER_ALREADY_REGISTERED') {
                setInfoMessage(t('admin.userAlreadyRegistered'));
            } else if (code === 'INVITE_ALREADY_PENDING') {
                setInfoMessage(t('admin.inviteAlreadyPending'));
            } else {
                setInfoMessage(t('admin.inviteError'));
            }
        });
    };

    const deleteClient = () => {
        setShowPopup(false);
        setShowDeleteModal(true);
    };

    const enterAccount = async () => {
        setShowPopup(false);
        try {
            const res = await axiosInstance.post('impersonate/', {
                corporate_group_id: currentCorporateGroup.id
            });
            // Save superuser credentials for restore
            localStorage.setItem('superuser_access_token', localStorage.getItem('access_token'));
            localStorage.setItem('superuser_refresh_token', localStorage.getItem('refresh_token'));
            localStorage.setItem('superuser_company_id', localStorage.getItem('company_id') || '');
            localStorage.setItem('superuser_project_id', localStorage.getItem('project_id') || '');
            localStorage.setItem('superuser_dashboard_id', localStorage.getItem('dashboard_id') || '');
            localStorage.setItem('impersonating_name', res.data.corporate_group.name);
            // Set client credentials
            localStorage.setItem('access_token', res.data.access_token);
            localStorage.setItem('refresh_token', res.data.refresh_token);
            if (res.data.company_id) localStorage.setItem('company_id', String(res.data.company_id));
            else localStorage.removeItem('company_id');
            if (res.data.project_id) {
                localStorage.setItem('project_id', String(res.data.project_id));
                localStorage.setItem('project', JSON.stringify(res.data.project || {}));
            } else {
                localStorage.removeItem('project_id');
                localStorage.removeItem('project');
            }
            localStorage.removeItem('dashboard_id');
            // Fetch client's projects (offices) so sidebar shows the right ones
            if (res.data.company_id) {
                try {
                    const projRes = await axiosInstance.get(`project/?company_id=${res.data.company_id}`);
                    const projects = Array.isArray(projRes.data) ? projRes.data : (projRes.data?.results || []);
                    localStorage.setItem('offices', JSON.stringify(projects));
                } catch (_) {
                    localStorage.removeItem('offices');
                }
            } else {
                localStorage.removeItem('offices');
            }
            // Navigate to client's view (full reload to re-init Context)
            window.location.href = '/resources';
        } catch (err) {
            const msg = err?.response?.data?.error || 'Unexpected error';
            setInfoMessage(t('admin.couldNotAccess', {msg}));
        }
    };

    return (
        <div style={{width: '200px', height: 'auto'}}>
            <div className={styles.popupButton} onClick={() => {
                setShowModal(true);
                setShowPopup(false);
                setForm(currentCorporateGroup);
            }}>
                <FontAwesomeIcon icon={faEdit} color={'#64748B'} className={'me-2'}/>
                <div style={{color: '#334155'}}>{t('common.edit')}</div>
            </div>
            <div className={styles.popupButton} onClick={() => sendInvite()}>
                <FontAwesomeIcon icon={faMailForward} color={'#64748B'} className={'me-2'}/>
                <div style={{color: '#334155'}}>{t('admin.resendInvite')}</div>
            </div>
            <div className={styles.popupButton} onClick={() => enterAccount()}>
                <FontAwesomeIcon icon={faRightToBracket} color={'#3B82F6'} className={'me-2'}/>
                <div style={{color: '#3B82F6'}}>{t('admin.enterAccount')}</div>
            </div>
            <div className={styles.popupButton} onClick={() => deleteClient()}>
                <FontAwesomeIcon icon={faTrash} color={'#EF4444'} className={'me-2'}/>
                <div style={{color: '#EF4444'}}>{t('common.delete')}</div>
            </div>
        </div>
    )
}

export default function AdminDashboard() {
    const {t} = useTranslation();
    const {refreshCorporateGroup, setSystemSettings: setGlobalSystemSettings} = useContext(MyContext);
    const [showPopup, setShowPopup] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: null, y: null });
    const [corporateGroup, setCorporateGroup] = useState({});
    const [form, setForm] = useState({});
    const [rows, setRows] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [infoMessage, setInfoMessage] = useState('');
    const [channelFlags, setChannelFlags] = useState({sms_enabled: false, whatsapp_enabled: false});
    const popupRef = useRef(null);

    useEffect(() => {
        axiosInstance.get('corporate_group/').then((res) => {
            setRows(res.data.results);
        });
        axiosInstance.get('system_settings/').then((res) => {
            setChannelFlags(res.data);
        }).catch(() => {});
    }, []);

    const toggleChannel = (key) => {
        const next = !channelFlags[key];
        axiosInstance.patch('system_settings/1/', {[key]: next}).then((res) => {
            setChannelFlags(res.data);
            if (setGlobalSystemSettings) {
                setGlobalSystemSettings(res.data);
            }
        });
    };

    const toggleDevMode = (id, value) => {
        axiosInstance.patch(`corporate_group/${id}/`, {dev_mode: value}).then(() => {
            setRows(prev => prev.map(r => r.id === id ? {...r, dev_mode: value} : r));
            refreshCorporateGroup();
        });
    };

    const toggleStatus = (id, currentStatus) => {
        const newStatus = currentStatus === 0 ? 1 : 0;
        axiosInstance.patch(`corporate_group/${id}/`, {status: newStatus}).then(() => {
            setRows(prev => prev.map(r => r.id === id ? {...r, status: newStatus} : r));
        });
    };

    const formatTimestamp = (timestamp) => {
        if(timestamp == null) return "-";
        const date = new Date(timestamp);
        return date.toLocaleString();
    }

    const cols = [
        {title: t('admin.columns.name'), internal: 'name' },
        {title: t('admin.columns.status'), internal: 'status', code: (row, data) => {
            return (
                <FontAwesomeIcon
                    icon={data === 0 ? faToggleOn : faToggleOff}
                    color={data === 0 ? '#22C55E' : '#94A3B8'}
                    size="lg"
                    style={{cursor: 'pointer'}}
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleStatus(row.id, data);
                    }}
                />
            );
        }},
        {title: t('admin.columns.contactName'), internal: 'contact_name' },
        {title: t('admin.columns.contactEmail'), internal: 'contact_email' },
        {title: t('admin.columns.dateJoined'), internal: 'created_at', code: (row, data) => {
            return formatTimestamp(data);
        }},
        {title: t('admin.columns.devMode'), internal: 'dev_mode', code: (row, data) => {
            return (
                <FontAwesomeIcon
                    icon={data ? faToggleOn : faToggleOff}
                    color={data ? '#22C55E' : '#94A3B8'}
                    size="lg"
                    style={{cursor: 'pointer'}}
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleDevMode(row.id, !data);
                    }}
                />
            );
        }},
        {title: t('admin.columns.actions'), internal: '', code: (row, data) => {
            return (
                <div className={`d-flex flex-row justify-content-center align-items-center ps-3 ${styles.editButton}`} onClick={(event) => {
                    setShowPopup(true);
                    setMousePosition({ x: event.clientX, y: event.clientY });
                    setCorporateGroup(row);
                    setForm(row);
                }}/>
            )
        }},
    ];

    const handleOutsideClick = (event) => {
        if (popupRef.current && !popupRef.current.contains(event.target)) {
            setShowPopup(false);
        }
    };

    useEffect(() => {
        if (showPopup) {
            document.addEventListener('mousedown', handleOutsideClick);
        } else {
            document.removeEventListener('mousedown', handleOutsideClick);
        }

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [showPopup]);

    const create = () => {
        let formData = new FormData();
        for(const key in form) {
            formData.append(key, form[key]);
        }

        if(Object.keys(corporateGroup).length > 0) {
            axiosInstance.put('corporate_group/' + corporateGroup.id + '/', formData).then((res) => {
                window.location.reload();
            });
        } else {
            axiosInstance.post('corporate_group/', formData).then((res) => {
                window.location.reload();
            });
        }
    };

    return (
        <div className={sidebarStyles.adminContent}>
            <AdminSidebar active={0}/>

            {showPopup &&
                <div ref={popupRef} className={styles.popUpModule} style={{top: mousePosition.y + 15, left: mousePosition.x - 180, position: 'absolute', zIndex: '100000'}}>
                    <PopupSettings currentCorporateGroup={corporateGroup} setShowPopup={setShowPopup} setShowModal={setShowModal} setForm={setForm} setRows={setRows} setShowDeleteModal={setShowDeleteModal} setInfoMessage={setInfoMessage}/>
                </div>
            }

            <Modal show={showModal} setter={setShowModal} name={Object.keys(corporateGroup).length > 0 ? 'Edit Client' : 'Create new Client'} buttonName={Object.keys(corporateGroup).length > 0 ? 'Save' :'Create'} onClick={create}>
                <LogoInput name={'Corporate Group Logo'} internalName={'logo'} form={form} setForm={setForm} hideName={true} current={corporateGroup?.image?.image}/>
                <TextInput name={'name'} form={form} setForm={setForm}/>
                <TextInput name={'Contact Name'} internalName={'contact_name'} form={form} setForm={setForm}/>
                <TextInput name={'Contact Email'} internalName={'contact_email'} form={form} setForm={setForm}/>

            </Modal>

            <Modal show={!!infoMessage} setter={() => setInfoMessage('')} name={'Notice'} buttonName={'OK'} onClick={() => setInfoMessage('')} hideCancel={true}>
                <p>{infoMessage}</p>
            </Modal>

            <Modal show={showDeleteModal} setter={setShowDeleteModal} name={'Delete Client'} buttonName={'Delete'} danger={true} onClick={() => {
                axiosInstance.delete(`corporate_group/${corporateGroup.id}/`).then(() => {
                    setRows(prev => prev.filter(r => r.id !== corporateGroup.id));
                    setShowDeleteModal(false);
                });
            }}>
                <p>Are you sure you want to delete <strong>{corporateGroup.name}</strong>?</p>
                <p style={{color: '#EF4444'}}>All related data will be permanently deleted (companies, projects, devices, users, subscription).</p>
            </Modal>

            <div className={'mx-5'}>
                <div style={{marginTop: '24px', marginBottom: '24px', padding: '16px 20px', border: '1px solid #E2E8F0', borderRadius: '8px', background: 'white'}}>
                    <div style={{fontSize: '15px', fontWeight: 600, color: '#1E293B', marginBottom: '4px'}}>Notification Channels</div>
                    <div style={{fontSize: '12px', color: '#64748B', marginBottom: '12px'}}>Global kill switches. When OFF, the channel is hidden from all users and any send attempt is silently dropped.</div>
                    <div style={{display: 'flex', gap: '32px', alignItems: 'center'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <FontAwesomeIcon
                                icon={channelFlags.sms_enabled ? faToggleOn : faToggleOff}
                                color={channelFlags.sms_enabled ? '#22C55E' : '#94A3B8'}
                                size="lg"
                                style={{cursor: 'pointer'}}
                                onClick={() => toggleChannel('sms_enabled')}
                            />
                            <span style={{fontSize: '13px', color: '#334155'}}>SMS</span>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <FontAwesomeIcon
                                icon={channelFlags.whatsapp_enabled ? faToggleOn : faToggleOff}
                                color={channelFlags.whatsapp_enabled ? '#22C55E' : '#94A3B8'}
                                size="lg"
                                style={{cursor: 'pointer'}}
                                onClick={() => toggleChannel('whatsapp_enabled')}
                            />
                            <span style={{fontSize: '13px', color: '#334155'}}>WhatsApp</span>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.6}}>
                            <FontAwesomeIcon icon={faToggleOn} color={'#22C55E'} size="lg"/>
                            <span style={{fontSize: '13px', color: '#334155'}}>Email (always on)</span>
                        </div>
                    </div>
                </div>

                <PageTitle title={'Clients'} createName={'Clients'} openModal={() => {
                    setCorporateGroup({});
                    setForm({});
                    setShowModal(true);
                }}/>

                <div className={'mt-3  pb-5'}>
                    <TableComponent cols={cols} rows={rows} pull={setRows}/>
                </div>
            </div>
        </div>
    );
}
